import { useState } from 'react';
import dayjs from 'dayjs';
import LogDisplay from 'client/js/util/log_display';
import PhpErrorLogFilters from './php_error_log_filters';
import Callout from 'client/js/util/callout';
import { useTimeRangePicker } from 'client/js/util/use_time_range_picker';

// Map PHP error levels to CSS classes
const getPhpErrorLevelClasses = (level) => {
  switch (level) {
    case 'fatal_error':
    case 'parse_error':
      return { border: 'border-log-critical', badge: 'badge-log-critical' };
    case 'error':
      return { border: 'border-log-error', badge: 'badge-log-error' };
    case 'warning':
      return { border: 'border-log-warning', badge: 'badge-log-warning' };
    case 'deprecated':
      return { border: 'border-log-info', badge: 'badge-log-info' };
    default:
      return { border: 'border-log-unknown', badge: 'badge-log-unknown' };
  }
};

// Format error level for display
const formatErrorLevel = (level) => {
  if (!level) return 'UNKNOWN';
  return level.replace('_', ' ').toUpperCase();
};

// Clean script path for display
const cleanScriptPath = (script) => {
  if (!script) return 'N/A';
  return script.replace(/\/home\/webpages\/lima-city\/[^\/]+\//, '');
};

// Validation functions for PHP error log filters
const validateFilters = (filters, getTimestamps) => {
  const errors = {};

  // Script path validation
  if (filters.script) {
    const script = filters.script.trim();
    if (script.length > 500) {
      errors.script = 'Script-Pfad zu lang (max. 500 Zeichen)';
    }
  }

  // Error level validation
  if (filters.level) {
    const level = filters.level.trim();
    const validLevels = ['deprecated', 'notice', 'info', 'warning', 'error', 'fatal_error', 'parse_error'];
    if (!validLevels.includes(level)) {
      errors.level = `Ungültiger Error-Level (muss einer sein von: ${validLevels.join(', ')})`;
    }
  }

  // Message validation
  if (filters.message) {
    const message = filters.message.trim();
    if (message.length > 500) {
      errors.message = 'Nachricht zu lang (max. 500 Zeichen)';
    }
  }

  // Time validation - use the hook to get current timestamps
  if (filters.timeRange && getTimestamps) {
    const timestamps = getTimestamps();
    if (timestamps) {
      try {
        const parsedTime = new Date(timestamps.from_time);
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);

        if (isNaN(parsedTime.getTime())) {
          errors.timeRange = 'Ungültiges Zeitformat';
        } else if (parsedTime > new Date()) {
          errors.timeRange = 'Zeit kann nicht in der Zukunft liegen';
        } else if (parsedTime < oneDayAgo - (60 * 1000)) { // Allow 1 minute tolerance
          errors.timeRange = 'Zeit kann nicht älter als 1 Tag sein';
        }
      } catch (e) {
        errors.timeRange = 'Ungültiges Zeitformat';
      }
    }
  }

  return { isValid: Object.keys(errors).length === 0, errors };
};

const Index = ({ endpoint = '/usercp/php_error_log', queryKey = 'php_error_log' }) => {
  const [filters, setFilters] = useState({
    script: '',
    level: '',
    message: '',
    timeRange: null
  });

  // Use the time range picker hook for validation
  const { getTimestamps } = useTimeRangePicker(filters.timeRange);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      script: '',
      level: '',
      message: '',
      timeRange: null
    });
  };

  // Wrap validateFilters to pass getTimestamps
  const validateFiltersWithTimestamps = (filters) => {
    return validateFilters(filters, getTimestamps);
  };

  const renderLogItem = (item) => {
    const levelClasses = getPhpErrorLevelClasses(item.level);
    const cleanScript = cleanScriptPath(item.script);

    return (
      <>
        <span className="log-timestamp">
          {dayjs(item.time).format('DD.MM.YYYY HH:mm:ss')}
        </span>
        <span className="log-hostname">
          {cleanScript}
        </span>
        <span className="log-client-ip">
          {item.script_line ? `Zeile ${item.script_line}` : 'N/A'}
        </span>
        <span className="log-message">
          {item.message || 'No message'}
        </span>
        <span className={`log-level ${levelClasses.badge}`}>
          {formatErrorLevel(item.level)}
        </span>
      </>
    );
  };

  const renderLogDetails = (item) => {
    const levelClasses = getPhpErrorLevelClasses(item.level);
    const cleanScript = cleanScriptPath(item.script);

    return (
      <>
        <span className="log-details-label">Zeitstempel:</span>
        <span className="log-details-value">{dayjs(item.time).format('DD.MM.YYYY HH:mm:ss')}</span>

        <span className="log-details-label">Error Level:</span>
        <span className="log-details-value">
          <span className={`log-level ${levelClasses.badge}`}>
            {formatErrorLevel(item.level)}
          </span>
        </span>

        <span className="log-details-label">Script:</span>
        <span className="log-details-value">{cleanScript}</span>

        <span className="log-details-label">Zeile:</span>
        <span className="log-details-value">{item.script_line || 'N/A'}</span>

        <span className="log-details-label">Vollständiger Pfad:</span>
        <span className="log-details-value">{item.script || 'N/A'}</span>

        <span className="log-details-label">Nachricht:</span>
        <span className="log-details-value log-message-full">{item.message || 'No message'}</span>

        {item.backtrace && item.backtrace.length > 0 && (
          <>
            <span className="log-details-label">Backtrace:</span>
            <span className="log-details-value">
              <pre className="log-message-full" style={{ fontSize: '12px', backgroundColor: '#f8f9fa', padding: '8px', borderRadius: '4px', maxHeight: '300px', overflow: 'auto' }}>
                {item.backtrace.map((trace) =>
                  trace.replace(/#([0-9]+) \/home\/webpages\/lima-city\/[^\/]+\//, '#$1 ')
                ).join('\n')}
              </pre>
            </span>
          </>
        )}
      </>
    );
  };

  // Custom no data message for PHP errors
  const NoDataMessage = () => (
    <Callout calloutClass="success" title="Alles in Ordnung!">
      <p>Es wurden keine PHP-Fehler oder Warnungen aufgezeichnet!</p>
    </Callout>
  );

  return (
    <LogDisplay
      endpoint={endpoint}
      queryKey={queryKey}
      filters={filters}
      onFilterChange={handleFilterChange}
      validateFilters={validateFiltersWithTimestamps}
      clearFilters={clearFilters}
      renderLogItem={renderLogItem}
      renderLogDetails={renderLogDetails}
      FilterSection={PhpErrorLogFilters}
      NoDataMessage={NoDataMessage}
    />
  );
};

export default Index;
